/**
 * useProspect Hook
 * Fetches prospect data by token and tracks initial page view
 */

import { useState, useEffect, useCallback } from 'react';
import { prospectAPI, trackingAPI, apiErrors } from '../utils/api';
import { mergeConfigWithDefaults } from '../utils/sections';

const STATUS_TYPES = {
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
  EXPIRED: 'expired',
  ACCEPTED: 'accepted',
};

/**
 * useProspect hook - manages prospect data fetching and status
 * @param {string} token - Public prospect token from URL
 * @returns {Object} - { prospect, status, error, isLoading, sessionId, refetch }
 */
export function useProspect(token) {
  const [prospect, setProspect] = useState(null);
  const [status, setStatus] = useState(STATUS_TYPES.LOADING);
  const [error, setError] = useState(null);
  const [sessionId] = useState(() => generateSessionId());

  // Generate unique session ID for this visit
  function generateSessionId() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  // Fetch prospect data
  const fetchProspect = useCallback(async () => {
    if (!token) {
      setStatus(STATUS_TYPES.ERROR);
      setError(new Error('No token provided'));
      return;
    }

    try {
      setStatus(STATUS_TYPES.LOADING);
      setError(null);

      const data = await prospectAPI.getProspectByToken(token);

      // Check if token is expired
      if (!data || data.expired) {
        setStatus(STATUS_TYPES.EXPIRED);
        setError(new Error('Portal link has expired'));
        return;
      }

      // Check if already accepted
      if (data.status === 'accepted') {
        setStatus(STATUS_TYPES.ACCEPTED);
      } else {
        setStatus(STATUS_TYPES.SUCCESS);
      }

      // Normalize section config
      const sectionConfig = mergeConfigWithDefaults(
        data.sectionConfig,
        data.tier || 'smb'
      );

      const normalizedProspect = {
        ...data,
        sectionConfig,
        sessionId,
      };

      setProspect(normalizedProspect);

      // Track initial page view
      try {
        await trackingAPI.recordView(data.id, sessionId);
      } catch (trackingError) {
        console.warn('Failed to record view:', trackingError);
        // Don't fail the entire flow if tracking fails
      }
    } catch (err) {
      console.error('Failed to fetch prospect:', err);

      if (apiErrors.isTokenExpired(err)) {
        setStatus(STATUS_TYPES.EXPIRED);
        setError(new Error('Portal link has expired'));
      } else if (apiErrors.isNotFound(err)) {
        setStatus(STATUS_TYPES.ERROR);
        setError(new Error('Prospect not found'));
      } else if (apiErrors.isServerError(err)) {
        setStatus(STATUS_TYPES.ERROR);
        setError(new Error('Server error. Please try again later.'));
      } else if (apiErrors.isNetworkError(err)) {
        setStatus(STATUS_TYPES.ERROR);
        setError(new Error('Network error. Please check your connection.'));
      } else {
        setStatus(STATUS_TYPES.ERROR);
        setError(err);
      }
    }
  }, [token, sessionId]);

  // Fetch on mount or when token changes
  useEffect(() => {
    fetchProspect();
  }, [token, fetchProspect]);

  return {
    prospect,
    status,
    error,
    isLoading: status === STATUS_TYPES.LOADING,
    isExpired: status === STATUS_TYPES.EXPIRED,
    isAccepted: status === STATUS_TYPES.ACCEPTED,
    isError: status === STATUS_TYPES.ERROR,
    sessionId,
    refetch: fetchProspect,
  };
}

/**
 * useProspectStatus hook - check current status of a prospect
 * Useful for polling or checking if status has changed
 */
export function useProspectStatus(prospectId, pollInterval = null) {
  const [prospectStatus, setProspectStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const checkStatus = useCallback(async () => {
    if (!prospectId) return;

    try {
      setIsLoading(true);
      const response = await prospectAPI.checkProspectStatus(prospectId);
      setProspectStatus(response.status);
    } catch (err) {
      console.error('Failed to check prospect status:', err);
    } finally {
      setIsLoading(false);
    }
  }, [prospectId]);

  useEffect(() => {
    checkStatus();

    if (!pollInterval) return;

    const interval = setInterval(checkStatus, pollInterval);
    return () => clearInterval(interval);
  }, [prospectId, pollInterval, checkStatus]);

  return { prospectStatus, isLoading, checkStatus };
}

export default useProspect;
